if (!customElements.get('product-preview-addon')) {
  customElements.define(
    'product-preview-addon',
    class ProductPreviewImage extends HTMLElement {
      static observedAttributes = ["data-sku"];
      /**
       * Image
       * @type {HTMLImageElement}
       */
      image = undefined;

      /**
       * @type {AbortController}
       */
      abortController = undefined;
      eventSource = undefined;

      activeBatchId = undefined;
      lightbox = undefined;

      /**
       * Event subscriber
       *
       * @type {*}
       */
      onFileStackFileChoiceUnsubscriber = undefined;

      /**
       * Photo url
       *
       * @type {string}
       */
      photoFileUrl = undefined;

      constructor() {
        super();
        this.image = this.querySelector('img');
      }

      /**
       * On connected
       */
      connectedCallback() {
        this.onFileStackFileChoice = subscribe(
          PUB_SUB_EVENTS.filestackFileChoiced,
          this.onPhotoChanged.bind(this),
        );
        this.initPhotoSwipe();
      }

      /**
       * On disconnected
       */
      disconnectedCallback() {
        this.onFileStackFileChoiceUnsubscriber?.();
        this.lightbox?.destroy();
        this.lightbox = undefined;
      }

      /**
       * On change photo
       *
       * @param url
       */
      onPhotoChanged({ data: { url } }) {
        this.photoFileUrl = url;
        this.processImageRender();
      }

      /**
       * Render image
       */
      processImageRender() {
        if (this.productSku === '') return;

        if (this.getAttribute('preloader')) {
          this.image.setAttribute('src', window.preloaderPreviewImage.src);
        }
        
        this.abortController?.abort();
        this.abortController = new AbortController();
        if (this.eventSource) {
          this.eventSource.close();
          this.eventSource = undefined;
        }

        if (this.activeBatchId) {
          this.cancelBatch(this.activeBatchId);
          this.activeBatchId = undefined;
        }

        fetch(this.baseUrl.concat('/api/v2/templates/', this.productSku), {
          signal: this.abortController.signal
        })
          .then((response) => response.json())
          .then(({ images }) => {
            if (!Array.isArray(images) || images.length === 0) {
              return;
            }
            const templateId = images[0].id;
            return fetch(this.baseUrl.concat('/api/v2/previews'), {
              method: 'POST',
              signal: this.abortController.signal,
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                sku: this.productSku,
                template_ids: [templateId],
                source_image_url: this.optimizePhotoUrl(this.photoFileUrl),
                source_font: document.querySelector('[name="properties[Font]"]')?.value || 'Georgia',
                source_text: document.querySelector('[name="properties[Engrave Text]"]')?.value || '',
              })
            })
              .then((response) => response.json())
              .then((batch) => {
                const items = Array.isArray(batch.items) ? batch.items : [];
                if (!items.length) {
                  this.activeBatchId = undefined;
                  return;
                }
                this.activeBatchId = batch.batch_id;
                const itemId = items[0].item_id;
                if (!itemId) {
                  this.activeBatchId = undefined;
                  return;
                }
                if (items[0].status === 'cached' && items[0].result_url) {
                  const mediumUrl = items[0].medium_url || items[0].mediumUrl || items[0].medium;
                  this.updatePreviewUrls(items[0].result_url, mediumUrl);
                  this.activeBatchId = undefined;
                  return;
                }
                this.attachEventSource(batch.batch_id, itemId);
              });
          })
      }

      cancelBatch(batchId) {
        if (!batchId) {
          return;
        }
        fetch(this.baseUrl.concat(`/api/v2/previews/${batchId}/cancel`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }).catch(() => {});
      }

      attachEventSource(batchId, itemId) {
        if (!window.EventSource) {
          this.pollBatch(batchId, itemId);
          return;
        }
        this.eventSource = new EventSource(this.baseUrl.concat(`/api/v2/previews/${batchId}/events`));
        const handleItem = (event) => {
          const data = JSON.parse(event.data || '{}');
          if (data.item_id !== itemId) {
            return;
          }
          if (data.result_url) {
            const mediumUrl = data.medium_url || data.mediumUrl || data.medium;
            this.updatePreviewUrls(data.result_url, mediumUrl);
            this.eventSource?.close();
            this.activeBatchId = undefined;
          }
        };
        this.eventSource.addEventListener('item.completed', handleItem);
        this.eventSource.addEventListener('item.cached', handleItem);
        this.eventSource.onerror = () => {
          this.eventSource?.close();
          this.pollBatch(batchId, itemId);
        };
      }

      pollBatch(batchId, itemId) {
        const startedAt = Date.now();
        const pollOnce = () => {
          if (Date.now() - startedAt > 45000) {
            console.warn("Timeout 45s for generate image");
            this.activeBatchId = undefined;
            return;
          }
          fetch(this.baseUrl.concat(`/api/v2/previews/${batchId}`), {
            signal: this.abortController.signal,
            cache: "no-store"
          })
            .then(response => response.json())
            .then((batch) => {
              const items = Array.isArray(batch.items) ? batch.items : [];
              const item = items.find((entry) => entry.item_id === itemId);
              if (item && (item.status === 'completed' || item.status === 'cached') && item.result_url) {
                const mediumUrl = item.medium_url || item.mediumUrl || item.medium;
                this.updatePreviewUrls(item.result_url, mediumUrl);
                this.activeBatchId = undefined;
                return;
              }
              if (item && item.status === 'failed') {
                this.activeBatchId = undefined;
                return;
              }
              setTimeout(pollOnce, 5000);
            });
        };
        pollOnce();
      }

      /**
       * Optimize image link
       *
       * @param url
       * @returns {string}
       */
      optimizePhotoUrl(url) {
        let image_url_object = new URL(url);
        if (image_url_object.hostname !== 'filestackcontent.com' && !image_url_object.hostname.endsWith('.filestackcontent.com')) return url;
        let image_name = image_url_object.pathname.replace('/', '');
        return 'https://cdn.filestackcontent.com/' + window.legacyPhotoTransformKey + '/resize=h:600,f:max/output=f:jpg/' +
          image_name;
      }

      updatePreviewUrls(fullUrl, mediumUrl) {
        const displayUrl = mediumUrl || fullUrl;
        const linkUrl = fullUrl || mediumUrl;
        if (displayUrl) {
          this.image.removeAttribute('srcset');
          this.image.setAttribute('src', displayUrl);
        }
        const link = this.querySelector('.preview-addon-link');
        if (link) {
          if (displayUrl) {
            link.setAttribute('href', displayUrl);
          }
          if (linkUrl) {
            link.setAttribute('data-pswp-src', linkUrl);
          }
        }
      }

      initPhotoSwipe() {
        if (typeof PhotoSwipeLightbox === 'undefined' || typeof PhotoSwipe === 'undefined') {
          return;
        }
        if (!this.querySelector('.preview-addon-link')) {
          return;
        }
        if (this.lightbox) {
          this.lightbox.destroy();
        }
        this.lightbox = new PhotoSwipeLightbox({
          gallery: this,
          children: '.preview-addon-link',
          pswpModule: PhotoSwipe,
          clickToCloseNonZoomable: false,
          mouseMovePan: true,
          initialZoomLevel: 'fit',
          secondaryZoomLevel: 1.5,
          maxZoomLevel: 4,
        });
        this.lightbox.init();
      }

      /**
       * On changed attribute (look: observedAttributes)
       *
       * @param name
       * @param oldValue
       * @param newValue
       */
      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'data-sku' && this.photoFileUrl) {
          this.processImageRender()
        }
        console.log(
          `Attribute ${name} has changed from ${oldValue} to ${newValue}.`,
        );
      }

      /**
       * Base url address
       *
       * @returns {string}
       */
      get baseUrl() {
        return window.routes?.apps_crystal_settings || '/apps/crystal-settings';
      }

      /**
       * Get product sku for image addon processor
       *
       * @returns {string}
       */
      get productSku() {
        if (this.dataset.sku.length === 15 || this.dataset.sku.length === 16) {
          return this.dataset.sku;
        }
        let sku = this.dataset.sku
          .replace('-CUSTOM', '')
          .replace('-CUSTM', '');
        if (sku.includes("-")) {
          return sku.concat("-CUSTM").slice(0, 16)
        }
        return sku.concat("-A-CUSTM").slice(0, 16);
      }

    }
  );
}
